import type { ProfileSlice, ProfileActions } from '@/types/store';
import type { UserProfile, CustomAllergen } from '@/types/profile';
import type { SliceSet } from './types';
import { detectDefaultMode, RegulatoryMode } from '@allergen-lens/shared-logic';

type Slice = ProfileSlice & ProfileActions;

export const createProfileSlice = (
  set: SliceSet
): Slice => ({
  profiles: [],
  activeProfileId: null,

  createProfile: (name: string) =>
    set((state) => {
      const now = new Date().toISOString();
      const profile: UserProfile = {
        id: `profile-${Date.now()}`,
        name,
        selectedAllergens: [],
        customAllergens: [],
        allergenSeverities: [],
        regulatoryMode: detectDefaultMode(),
        createdAt: now,
        updatedAt: now,
      };
      return {
        profiles: [...state.profiles, profile],
        activeProfileId: profile.id,
      };
    }),

  updateProfile: (updates: Partial<UserProfile>) =>
    set((state) => ({
      profiles: state.profiles.map((p) =>
        p.id === state.activeProfileId
          ? { ...p, ...updates, updatedAt: new Date().toISOString() }
          : p
      ),
    })),

  deleteProfile: (id: string) =>
    set((state) => ({
      profiles: state.profiles.filter((p) => p.id !== id),
      activeProfileId: state.activeProfileId === id ? null : state.activeProfileId,
    })),

  setActiveProfile: (id: string | null) =>
    set((state) => {
      if (id === null) return { activeProfileId: null };
      const profile = state.profiles.find((p) => p.id === id);
      if (!profile) return { activeProfileId: id };
      if (profile.regulatoryMode && profile.regulatoryMode !== state.mode) {
        return { activeProfileId: id, mode: profile.regulatoryMode as RegulatoryMode };
      }
      return { activeProfileId: id };
    }),

  toggleProfileAllergen: (allergenId: string) =>
    set((state) => {
      const profile = state.profiles.find((p) => p.id === state.activeProfileId);
      if (!profile) return state;

      const selected = profile.selectedAllergens.includes(allergenId)
        ? profile.selectedAllergens.filter((a) => a !== allergenId)
        : [...profile.selectedAllergens, allergenId];

      return {
        profiles: state.profiles.map((p) =>
          p.id === state.activeProfileId
            ? { ...p, selectedAllergens: selected, updatedAt: new Date().toISOString() }
            : p
        ),
      };
    }),

  addCustomAllergen: (allergen: CustomAllergen) =>
    set((state) => ({
      profiles: state.profiles.map((p) =>
        p.id === state.activeProfileId
          ? { ...p, customAllergens: [...p.customAllergens, allergen], updatedAt: new Date().toISOString() }
          : p
      ),
    })),

  removeCustomAllergen: (id: string) =>
    set((state) => ({
      profiles: state.profiles.map((p) =>
        p.id === state.activeProfileId
          ? { ...p, customAllergens: p.customAllergens.filter((a) => a.id !== id), updatedAt: new Date().toISOString() }
          : p
      ),
    })),
});
